import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import Page from 'src/components/Page';
import TabCrm from 'src/sections/@dashboard/home/TabCrm';
import MetricCard from 'src/sections/@dashboard/marketing/components/MetricCard';
import StatusChip from 'src/sections/@dashboard/marketing/components/StatusChip';
import PlatformChip from 'src/sections/@dashboard/marketing/components/PlatformChip';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';

// Shadcn Components
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';

// Utils
import { fCurrency, fPercent } from 'src/utils/formatNumber';
import { fDate, fDateTimeSuffix } from 'src/utils/formatTime';
import { serieColor } from 'src/design/tokens';

// Hooks
import useAuth from 'src/hooks/useAuth';

// Actions
import v2_actionCampanhaDetalhes from 'src/actions/v2/marketing/v2_actionCampanhaDetalhes';
import Iconify from 'src/components/Iconify';

import { MKT_METRICAS_DESIGN as d } from './MarketingMetricas.design';
import { getStatusMeta } from './MarketingRastreamento.design';

const STATUS_TONE = {
  ativa: 'success',
  pausada: 'warning',
  finalizada: 'neutral',
  erro: 'danger',
};

// ----------------------------------------------------------------------

export default function MarketingMetricas() {
  const { t } = useTranslation('marketing');
  const { cliente } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const campanhaId = searchParams.get('campanha');
  const campanhaNome = searchParams.get('nome');

  // Estados
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [campanhaData, setCampanhaData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [canalSelecionado, setCanalSelecionado] = useState(cliente._id);
  const [periodo, setPeriodo] = useState('30');
  const [dadosEstados, setDadosEstados] = useState([]);
  const [chatsCapturados, setChatsCapturados] = useState([]);

  // Buscar dados da campanha
  useEffect(() => {
    if (campanhaId) {
      buscarDadosCampanha();
    }
  }, [campanhaId]);

  // Buscar dados dos estados quando apiData estiver disponível
  useEffect(() => {
    if (apiData) {
      buscarDadosEstados();
    }
  }, [apiData]);

  // Ranking de anúncios (CTWA). Fica aqui em cima, antes dos returns de
  // loading/erro — hook depois de return condicional muda a ordem dos hooks
  // entre um render e outro e derruba a tela na transição.
  // O backend novo já manda `anunciosCtwa` pronto; a agregação a partir dos
  // chats é o fallback para instalação com backend antigo, que só devolve
  // ad_id/ad_name dentro de chatsCapturados.
  const anunciosRanking = useMemo(() => {
    const doBackend = campanhaData?.anuncios_ctwa;

    if (Array.isArray(doBackend) && doBackend.length > 0) {
      return [...doBackend]
        .map((anuncio) => ({
          ad_id: anuncio.ad_id || null,
          ad_name: anuncio.ad_name || null,
          capturas: anuncio.capturas || 0,
          ultimaCaptura: anuncio.ultimaCaptura || null,
        }))
        .sort((a, b) => b.capturas - a.capturas);
    }

    const porAnuncio = new Map();

    chatsCapturados.forEach((chat) => {
      // Chat sem ad_id veio por palavra-chave, não por anúncio — entrar no
      // ranking criaria um balde gigante "sem anúncio" que não é CTWA.
      if (!chat.ad_id) return;

      const atual = porAnuncio.get(chat.ad_id) || {
        ad_id: chat.ad_id,
        ad_name: chat.ad_name || null,
        capturas: 0,
        ultimaCaptura: null,
      };

      atual.capturas += 1;
      atual.ad_name = atual.ad_name || chat.ad_name || null;

      const data = chat.chat_data_criacao ? new Date(chat.chat_data_criacao) : null;
      if (data && (!atual.ultimaCaptura || data > new Date(atual.ultimaCaptura))) {
        atual.ultimaCaptura = chat.chat_data_criacao;
      }

      porAnuncio.set(chat.ad_id, atual);
    });

    return [...porAnuncio.values()].sort((a, b) => b.capturas - a.capturas);
  }, [campanhaData, chatsCapturados]);

  const buscarDadosCampanha = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await v2_actionCampanhaDetalhes(campanhaId);

      if (response.data.error) {
        throw new Error(response.data.message || t('metrics.loadError'));
      }

      const data = response.data.data;
      setApiData(data);

      // Mapear dados da API para a estrutura existente
      const campanhaMapeada = {
        _id: data.campanha.id,
        nome: campanhaNome ? decodeURIComponent(campanhaNome) : data.campanha.nome,
        plataforma: data.campanha.plataforma,
        status: data.campanha.status === 'active' ? 'ativa' : data.campanha.status,
        descricao: data.campanha.descricao,
        orcamento: parseFloat(data.orcamento.valor),
        gasto: parseFloat(data.orcamento.gastoTotal),
        impressoes: data.metricados?.impressoes?.valor || 0,
        cliques: data.metricados?.capturas?.valor || 0,
        conversoes: data.metricados?.conversaoLeads?.valor || 0,
        chats_criados: data.metricados?.chatsCriados?.valor || 0,
        conversao_chats: data.metricados?.conversaoChats?.valor || 0,
        cpc_metricas: data.metricados?.cpc,
        variacao_capturas: data.metricados?.capturas?.variacao,
        variacao_chats_criados: data.metricados?.chatsCriados?.variacao,
        variacao_conversao_chats: data.metricados?.conversaoChats?.variacao,
        leads_ctwa: data.metricados?.ctwa?.valor || 0,
        anuncios_ctwa: data.anunciosCtwa || [],
        canal_id: data.campanha.canal_id || null,
        ads: data.campanha.ads || [],
        // Status da varredura retroativa (job assincrono no lais-webhook).
        // null quando a campanha nunca disparou um job (modo palavra-chave,
        // ou modo anuncio criado sem data retroativa a buscar).
        backfill_retroativo: data.campanha.backfillRetroativo || null,
        publico_alvo: data.informacoes.publicoAlvo,
        regioes: data.informacoes.regioesAtuacao,
        horarios_pico: data.informacoes.horariosPico,
        palavras_chave: data.informacoes.palavrasChave && data.informacoes.palavrasChave.length > 0
          ? data.informacoes.palavrasChave
          : [],
        data_inicio: new Date(data.periodo.dataInicio),
        data_fim: new Date(data.periodo.dataFinal),
        dias_restantes: data.periodo.diasRestantes,
        kpis: data.kpis,
        percentual_utilizado: data.orcamento.percentualUtilizado
      };

      setCampanhaData(campanhaMapeada);

      // Chats capturados — guardados crus de proposito: cada item carrega
      // tambem ad_id/ad_name, usados na coluna de anuncio e no fallback de
      // agregacao por anuncio.
      if (data.chatsCapturados) {
        setChatsCapturados(data.chatsCapturados);
      }

    } catch (error) {
      console.error('Erro ao buscar dados da campanha:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const buscarDadosEstados = async () => {
    try {
      // Usar dados da API se disponíveis
      if (apiData && apiData.distribuicaoGeografica && apiData.distribuicaoGeografica.dddPorEstado) {
        const estadosApi = apiData.distribuicaoGeografica.dddPorEstado;

        // Mapear dados da API para o formato esperado
        const estados = estadosApi
          .map((estado) => ({
            nome: estado.nome,
            sigla: estado.sigla,
            capturas: estado.capturas || 0,
            corApi: estado.cor,
          }))
          // Ordenar por número de capturas (maior para menor) ANTES de sortear
          // a cor — assim o 1º colocado sempre pega o violeta da marca.
          .sort((a, b) => b.capturas - a.capturas)
          .map((estado, index) => ({
            ...estado,
            // Antes: `#${Math.floor(Math.random()*16777215).toString(16)}` —
            // cor fora da marca, sorteada de novo a cada refetch, e hex
            // malformado (< 6 digitos) sempre que o valor caia abaixo de
            // 0x100000. serieColor() e determinístico e usa a paleta da marca.
            cor: estado.corApi || serieColor(index),
          }));

        setDadosEstados(estados);
      }
    } catch (error) {
      console.log('Erro ao buscar dados dos estados:', error);
    }
  };

  const calcularDiasRestantes = () => {
    if (!campanhaData) return 0;
    return campanhaData.dias_restantes || 0;
  };

  const exportarChatsExcel = () => {
    if (!chatsCapturados.length) return;

    // Cabeçalho e linha são posicionais: a coluna de anúncio entra na mesma
    // posição (4ª) nos dois arrays, logo depois do nome.
    const headers = ['#', t('metrics.export.number'), t('metrics.export.name'), t('metrics.export.ad'), t('metrics.export.funnel'), t('metrics.export.stage'), t('metrics.export.createdAt')];
    const rows = chatsCapturados.map((chat, index) => [
      index + 1,
      chat.chat_numero || chat.phone || '',
      chat.chat_nome || '',
      chat.ad_name || chat.ad_id || '',
      chat.nome_funil || '',
      chat.estagio_funil || '',
      chat.chat_data_criacao ? fDateTimeSuffix(new Date(chat.chat_data_criacao)) : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `chats-capturados-${campanhaData?.nome || 'campanha'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleVoltar = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Page title={t('metrics.pageTitle')}>
        <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
          <div className={`${d.page.container} pt-8 ${d.page.pageBody}`}>
            <MktLoading text={t('metrics.loading')} />
          </div>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title={t('metrics.pageTitle')}>
        <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
          <div className={`${d.page.container} pt-8 ${d.page.pageBody}`}>
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-lg font-semibold text-[#18181b] mb-1">{t('metrics.errorTitle')}</p>
              <p className="text-sm text-[#71717a]">{error}</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if (!campanhaData) {
    return (
      <Page title={t('metrics.pageTitle')}>
        <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
          <div className={`${d.page.container} pt-8 ${d.page.pageBody}`}>
            <div className="flex items-center justify-center py-20">
              <p className="text-lg text-[#71717a]">{t('metrics.notFound')}</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const cpcFormatado = campanhaData.cpc_metricas?.valor != null
    ? `R$ ${parseFloat(campanhaData.cpc_metricas.valor).toFixed(2)}`
    : (campanhaData.kpis?.cpc || 'N/A');

  const taxaConversaoFormatada = fPercent(campanhaData.conversao_chats * 100);

  // Total de leads de anúncio: o backend novo manda em metricados.ctwa; a soma
  // do ranking cobre o fallback (backend antigo).
  const leadsCtwa = campanhaData.leads_ctwa || anunciosRanking.reduce((soma, anuncio) => soma + anuncio.capturas, 0);
  const topAnuncio = anunciosRanking[0];
  const maiorCaptura = topAnuncio?.capturas || 0;

  // Campanha sem canal CTWA configurado nunca captura por anúncio — a seção só
  // aparece quando há configuração ou dado, pra não poluir a tela das campanhas
  // antigas de palavra-chave. Configurada e ainda sem captura ela aparece
  // vazia, de propósito.
  const mostrarCtwa = Boolean(campanhaData.canal_id) || leadsCtwa > 0 || anunciosRanking.length > 0;

  // Banner da varredura retroativa (job assincrono do lais-webhook que busca
  // leads de referral anteriores a criacao da campanha). Sem job (null) nao
  // renderiza nada — cobre tanto campanha em modo palavra-chave quanto
  // campanha em modo anuncio sem periodo retroativo a varrer.
  const backfill = campanhaData.backfill_retroativo;
  let backfillBanner = null;
  if (backfill) {
    if (backfill.status === 'pending' || backfill.status === 'processing') {
      backfillBanner = { tone: 'info', text: t('metrics.ctwa.backfillPending') };
    } else if (backfill.status === 'done') {
      const criados = backfill.stats?.criados || 0;
      backfillBanner = criados > 0
        ? { tone: 'success', text: t('metrics.ctwa.backfillDone', { count: criados }) }
        : { tone: 'neutral', text: t('metrics.ctwa.backfillDoneEmpty') };
    } else if (backfill.status === 'error') {
      backfillBanner = { tone: 'warning', text: t('metrics.ctwa.backfillError') };
    }
  }

  const nomeAnuncio = (anuncio) => anuncio.ad_name || anuncio.ad_id || t('metrics.ctwa.noAd');

  // Nome de anúncio da Meta é longo ("[VD] Conjunto 03 - Remarketing 7d") e o
  // slot de valor do MetricCard não trunca — entra encurtado.
  const encurtarNome = (texto) => (texto && texto.length > 20 ? `${texto.slice(0, 19)}…` : texto);

  const statusTone = STATUS_TONE[campanhaData.status] ?? 'neutral';
  const statusLabel = getStatusMeta(campanhaData.status, t).label;

  return (
    <Page title={t('metrics.pageTitleNamed', { name: campanhaData.nome })} className="h-full">
      <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
        <div className={d.page.stickyHeader}>
          <div className={d.page.container}>
            <div className={d.page.topbar}>
              <div className={d.page.topbarIdentity}>
                <button type="button" onClick={handleVoltar} className={d.page.backButton} aria-label={t('common.back', 'Voltar')}>
                  <Iconify icon="eva:arrow-back-outline" width={18} />
                </button>
                <div className="min-w-0">
                  <h1 className={d.page.headerTitle}>{campanhaData.nome}</h1>
                  <p className={d.page.headerSubtitle}>{t('metrics.subtitle')}</p>
                </div>
              </div>
              <div className={d.page.topbarActions}>
                <StatusChip tone={statusTone} label={statusLabel} size="sm" />
                <PlatformChip platform={campanhaData.plataforma} size="sm" />
              </div>
            </div>
          </div>
        </div>

        <div className={`${d.page.container} pt-4 ${d.page.pageBody}`}>
        <div className={d.page.metaBar}>
          <div className={d.header.metaItem}>
            <div>
              <p className={d.header.metaLabel}>{t('metrics.meta.period')}</p>
              <p className={d.header.metaValue}>{fDate(campanhaData.data_inicio)} - {fDate(campanhaData.data_fim)}</p>
            </div>
          </div>

          <div className={d.header.metaItem}>
            <div>
              <p className={d.header.metaLabel}>{t('metrics.meta.budget')}</p>
              <p className={d.header.metaValue}>R$ {fCurrency(campanhaData.orcamento)}</p>
            </div>
          </div>

          <div className={d.header.metaItem}>
            <div>
              <p className={d.header.metaLabel}>{t('metrics.meta.daysRemaining')}</p>
              <p className={d.header.metaValue}>{calcularDiasRestantes()}</p>
            </div>
          </div>
        </div>

        {/* Palavras-chave */}
        {campanhaData.palavras_chave?.length > 0 && (
          <div className={`${d.header.keywordsWrap} mb-6`}>
            <span className={d.header.keywordsLabel}>
              {t('metrics.keywords', { count: campanhaData.palavras_chave.length })}
            </span>
            {campanhaData.palavras_chave.slice(0, 6).map((key, index) => (
              <span key={index} className={d.header.keywordChip}>
                {key.value}
              </span>
            ))}
            {campanhaData.palavras_chave.length > 6 && (
              <span className={d.header.keywordChipMore}>
                {t('metrics.keywordsMore', { count: campanhaData.palavras_chave.length - 6 })}
              </span>
            )}
          </div>
        )}

          {/* Métricas de Capturas e Análise Geográfica */}
          <div className={d.section.wrapper}>
            <div className={d.section.headerRow}>
              <h3 className={d.section.title}>{t('metrics.conversations.title')}</h3>
            </div>

            <div className={d.section.metricsGrid}>
              <MetricCard
                value={campanhaData.cliques}
                label={t('metrics.conversations.captured')}
                growth={campanhaData.variacao_capturas}
              />

              <MetricCard
                value={cpcFormatado}
                label={t('metrics.conversations.cpc')}
              />

              <MetricCard
                value={campanhaData.chats_criados}
                label={t('metrics.conversations.created')}
                growth={campanhaData.variacao_chats_criados}
              />

              <MetricCard
                value={taxaConversaoFormatada}
                label={t('metrics.conversations.conversionRate')}
                growth={campanhaData.variacao_conversao_chats}
              />
            </div>

            <Card className={d.table.card}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-foreground">
                  {t('metrics.geo.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dadosEstados.length === 0 ? (
                  <div className={d.emptyState.container}>
                    <p className={d.emptyState.title}>{t('metrics.geo.empty')}</p>
                  </div>
                ) : (
                  <div className="max-h-[60vh] overflow-y-auto">
                    <Table className="relative">
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className={`w-12 ${d.table.headCell}`}>#</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.geo.state')}</TableHead>
                          <TableHead className={`text-right ${d.table.headCell}`}>{t('metrics.geo.captures')}</TableHead>
                          <TableHead className={`text-right ${d.table.headCell}`}>%</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dadosEstados.map((estado, index) => {
                          const totalCapturas = dadosEstados.reduce((sum, e) => sum + e.capturas, 0);
                          const percentual = ((estado.capturas / totalCapturas) * 100).toFixed(1);

                          return (
                            <TableRow key={estado.sigla} className={d.table.row}>
                              <TableCell className="font-medium text-muted-foreground tabular-nums">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-semibold text-foreground">{estado.nome}</div>
                                  <div className="text-sm text-muted-foreground">{estado.sigla}</div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-bold text-foreground">
                                {estado.capturas.toLocaleString('pt-BR')}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-3">
                                  <div className={d.table.progressTrack}>
                                    <div
                                      className={d.table.progressFill}
                                      style={{
                                        width: `${percentual}%`,
                                        backgroundColor: estado.cor
                                      }}
                                    ></div>
                                  </div>
                                  <span className="text-sm font-semibold text-foreground w-10">
                                    {percentual}%
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Leads por anúncio (Click-to-WhatsApp) */}
          {mostrarCtwa && (
            <div className={d.section.wrapper}>
              <div className={d.section.headerRow}>
                <div>
                  <h3 className={d.section.title}>{t('metrics.ctwa.title')}</h3>
                  <p className={d.ctwa.subtitle}>{t('metrics.ctwa.subtitle')}</p>
                </div>
              </div>

              {backfillBanner && (
                <div className={`${d.ctwa.banner} ${d.tone[backfillBanner.tone]}`}>
                  <span>{backfillBanner.text}</span>
                </div>
              )}

              <div className={d.ctwa.grid}>
                <MetricCard
                  value={leadsCtwa}
                  label={t('metrics.ctwa.leads')}
                />

                <MetricCard
                  value={anunciosRanking.length}
                  label={t('metrics.ctwa.ads')}
                />

                <MetricCard
                  value={topAnuncio ? encurtarNome(nomeAnuncio(topAnuncio)) : '-'}
                  label={t('metrics.ctwa.topAd')}
                />
              </div>

              {anunciosRanking.length === 0 ? (
                <div className={d.emptyState.container}>
                  <p className={d.emptyState.title}>{t('metrics.ctwa.empty')}</p>
                </div>
              ) : (
                <Card className={d.table.card}>
                  <CardContent className="p-0">
                    <div className="max-h-[420px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border">
                            <TableHead className={`w-12 ${d.table.headCell}`}>#</TableHead>
                            <TableHead className={d.table.headCell}>{t('metrics.ctwa.tableAd')}</TableHead>
                            <TableHead className={`text-right ${d.table.headCell}`}>{t('metrics.ctwa.tableLeads')}</TableHead>
                            <TableHead className={`text-right ${d.table.headCell}`}>{t('metrics.ctwa.tableLast')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {anunciosRanking.map((anuncio, index) => {
                            const cor = serieColor(index);
                            // Barra proporcional ao 1º colocado (e não ao
                            // total): num ranking curto o total achata todas
                            // as barras e some a diferença entre elas.
                            const proporcao = maiorCaptura > 0 ? (anuncio.capturas / maiorCaptura) * 100 : 0;

                            return (
                              <TableRow key={anuncio.ad_id || `sem-anuncio-${index}`} className={d.table.row}>
                                <TableCell className="font-medium text-muted-foreground tabular-nums">
                                  {index + 1}
                                </TableCell>
                                <TableCell>
                                  <div className={d.ctwa.adName}>{nomeAnuncio(anuncio)}</div>
                                  {anuncio.ad_name && anuncio.ad_id && (
                                    <div className={d.ctwa.adId}>{anuncio.ad_id}</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-3">
                                    <div className={d.table.progressTrack}>
                                      <div
                                        className={d.table.progressFill}
                                        style={{ width: `${proporcao}%`, backgroundColor: cor }}
                                      ></div>
                                    </div>
                                    <span className={d.ctwa.leadsValue}>
                                      {anuncio.capturas.toLocaleString('pt-BR')}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right text-sm text-muted-foreground">
                                  {anuncio.ultimaCaptura ? fDateTimeSuffix(new Date(anuncio.ultimaCaptura)) : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Métricas de Leads (CRM) */}
          <div className={d.crm.wrapper}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={d.section.title}>{t('metrics.crm.title')}</h3>
            </div>

            {/* Componente TabCrm */}
            <TabCrm
              changeClienteId={setCanalSelecionado}
              changePeriodo={setPeriodo}
              clienteId={canalSelecionado}
              periodo={periodo}
              hidePeriod={true}
              hidePannels={['last_6_months']}
              campaingConfig={
                {
                  start_date: campanhaData.data_inicio,
                  end_date: campanhaData.data_fim,
                  campaign_id: campanhaData._id
                }
              }
            />
          </div>

          {/* Tabela de Chats Capturados */}
          {chatsCapturados.length > 0 && (
            <div className={d.chatsTable.wrapper}>
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h3 className={d.section.title}>
                  {t('metrics.chatsTable.title')}
                  <Badge variant="secondary" className="ml-2">{chatsCapturados.length}</Badge>
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportarChatsExcel}
                  className={d.exportBtn}
                >
                  {t('metrics.chatsTable.export')}
                </Button>
              </div>

              <Card className={d.table.card}>
                <CardContent className="p-0">
                  <div className="max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className={d.table.headCell}>#</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.chatsTable.number')}</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.chatsTable.name')}</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.chatsTable.ad')}</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.chatsTable.funnel')}</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.chatsTable.stage')}</TableHead>
                          <TableHead className={d.table.headCell}>{t('metrics.chatsTable.createdAt')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {chatsCapturados.map((chat, index) => (
                          <TableRow key={chat._id} className={d.table.row}>
                            <TableCell className="font-medium text-muted-foreground">{index + 1}</TableCell>
                            <TableCell className="font-medium text-foreground">
                              {chat.chat_numero || chat.phone || '-'}
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-foreground">
                                {chat.chat_nome || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              {chat.ad_name || chat.ad_id ? (
                                <span className={d.ctwa.chatAd} title={chat.ad_name || chat.ad_id}>
                                  {chat.ad_name || chat.ad_id}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {chat.nome_funil ? (
                                <Badge variant="outline" className="text-xs">
                                  {chat.nome_funil}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {chat.estagio_funil ? (
                                <Badge variant="secondary" className="text-xs">
                                  {chat.estagio_funil}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {chat.chat_data_criacao ? fDateTimeSuffix(new Date(chat.chat_data_criacao)) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

        </div>
      </div>
    </Page>
  );
}
